import React, { useState } from 'react';
import { Paper, Typography, Button, Stack, Alert } from '@mui/material';
import html2canvas from 'html2canvas';
import { generateShareSummary } from '../utils/shareText';

const SharePanel = ({ snapshot, shareTargetRef }) => {
  const [feedback, setFeedback] = useState(null);
  const [loading, setLoading] = useState(false);

  const captureSnapshot = async () => {
    if (!shareTargetRef?.current) {
      throw new Error('共有対象のセクションが見つかりません。');
    }
    const canvas = await html2canvas(shareTargetRef.current, {
      backgroundColor: '#ffffff',
      scale: 2,
    });
    const blob = await new Promise((resolve, reject) => {
      canvas.toBlob((blobResult) => {
        if (blobResult) {
          resolve(blobResult);
        } else {
          reject(new Error('画像の生成に失敗しました。'));
        }
      }, 'image/jpeg');
    });
    return blob;
  };

  const downloadBlob = (blob) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `pitch-counter-${Date.now()}.jpg`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleShare = async () => {
    setFeedback(null);
    setLoading(true);
    try {
      const blob = await captureSnapshot();
      const file = new File([blob], `pitch-counter-${Date.now()}.jpg`, { type: 'image/jpeg' });
      const nav = typeof navigator !== 'undefined' ? navigator : undefined;
      if (nav?.share && nav?.canShare?.({ files: [file] })) {
        await nav.share({
          files: [file],
          title: 'Pitch Counter Snapshot',
          text: 'Pitch Counter のスナップショットを共有します。',
        });
        setFeedback({ severity: 'success', message: '画像を共有しました。' });
      } else if (nav?.clipboard?.writeText) {
        await nav.clipboard.writeText(generateShareSummary(snapshot));
        setFeedback({
          severity: 'info',
          message: '画像共有に未対応のため、サマリーテキストをコピーしました。',
        });
      } else {
        downloadBlob(blob);
        setFeedback({
          severity: 'info',
          message: '画像をダウンロードしました。共有できない場合は手動で送信してください。',
        });
      }
    } catch (error) {
      setFeedback({ severity: 'error', message: error.message || '共有に失敗しました。' });
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    setFeedback(null);
    setLoading(true);
    try {
      const blob = await captureSnapshot();
      downloadBlob(blob);
      setFeedback({ severity: 'success', message: '画像をダウンロードしました。' });
    } catch (error) {
      setFeedback({ severity: 'error', message: error.message || '保存に失敗しました。' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper elevation={2} sx={{ p: 2, mt: 3 }}>
      <Stack spacing={1}>
        <Typography variant="h6">共有・エクスポート</Typography>
        <Typography variant="body2" color="text.secondary">
          タイトルからグラフまでを画像化して共有できます。非対応環境ではテキストコピーまたは画像ダウンロードになります。
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <Button variant="contained" onClick={handleShare} aria-label="共有" disabled={loading}>
            {loading ? '生成中...' : '共有'}
          </Button>
          <Button variant="outlined" onClick={handleDownload} aria-label="画像を保存" disabled={loading}>
            画像を保存
          </Button>
        </Stack>
        {feedback && (
          <Alert severity={feedback.severity} data-testid="share-feedback">
            {feedback.message}
          </Alert>
        )}
      </Stack>
    </Paper>
  );
};

export default SharePanel;
